<template>
    <div class="flex h-full flex-col font-inter">
        <div class="space-between mt-8 flex w-full px-8">
            <div class="flex flex-1 items-center justify-between gap-3">
                <h2 class="text-xl font-bold">
                    {{ isBusinessReport ? 'Business account verification report' : 'Bank account verification report' }}
                </h2>
                <div
                    v-if="reportResult?.bank_account_is_insured"
                    class="flex items-center gap-1 text-sm"
                >
                    <img
                        alt="Closinglock Shield"
                        height="15px"
                        src="/images/icon-insurance.png"
                        width="15px"
                    />
                    <span>Insured by Closinglock</span>
                </div>
            </div>
        </div>
        <Divider v-if="showReportSections" />
        <div class="flex flex-col gap-2 px-5">
            <div
                v-if="hasError"
                class="mt-4 rounded-lg bg-ds-red-100 p-4"
            >
                <p class="font-bold text-ds-red-600">Error</p>
                <p class="text-ds-red-600">Problem loading report. Please contact support.</p>
            </div>
            <div
                v-else-if="stillProcessing"
                class="mt-4 rounded-lg bg-ds-yellow-100 p-4"
            >
                <p class="font-bold text-ds-yellow-800">Report Processing</p>
                <p class="text-ds-yellow-800">The report is still processing. Please check back later.</p>
            </div>
            <template v-else-if="reportResult && showReportSections">
                <VerificationSection
                    class="bg-ds-primary-50 px-2"
                    :collapsable="false"
                    title="File Information"
                    :with-divider="false"
                >
                    <FileInformation :file-information="reportResult.file_information" />
                </VerificationSection>
                <VerificationSection
                    class="bg-ds-primary-50 px-2"
                    :collapsable="false"
                    title="Banking Information"
                    :with-divider="false"
                >
                    <BankingInformation
                        :additional-information="reportResult?.additional_information"
                        :banking-information="reportResult.banking_information"
                        :source-information="mergedSourceStatus"
                    />
                </VerificationSection>
                <VerificationSection
                    class="bg-ds-primary-50 px-2 capitalize"
                    :collapsable="false"
                    title="Account Status"
                    :with-divider="false"
                >
                    <AccountStatus :account-status="reportResult.account_status" />
                </VerificationSection>
                <VerificationSection
                    class="bg-ds-primary-50 px-2"
                    :collapsable="false"
                    title="Account Details"
                    :with-divider="false"
                >
                    <AccountDetails
                        :account-details="reportResult.account_details"
                        :isBusinessReport
                    />
                </VerificationSection>
            </template>
        </div>
        <template v-if="showReportSections">
            <Divider />
            <div class="flex flex-row items-end justify-end gap-1 px-8 text-sm">
                <DsButton
                    size="md"
                    variant="secondary"
                    @click="$emit('close')"
                >
                    Close
                </DsButton>
                <DsButton
                    :download="`report-${currentReportId}.pdf`"
                    :href="reportUrl"
                    leading="fas fa-file-pdf"
                    size="md"
                    target="_blank"
                    variant="primary"
                >
                    Download report
                </DsButton>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
import type { BAVReturnData } from '@/api/interfaces/bank-account-verification.api';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { useBankAccountVerificationService } from '@/services/bank-account-verification.service';
import Divider from 'primevue/divider';
import { computed, onBeforeUnmount, ref } from 'vue';
import VerificationSection from '../IdentityVerificationReport/common/VerificationSection.vue';
import AccountDetails from './reports/AccountDetails.vue';
import AccountStatus from './reports/AccountStatus.vue';
import BankingInformation from './reports/BankingInformation.vue';
import FileInformation from './reports/FileInformation.vue';

const bavService = useBankAccountVerificationService();

const reportUrl = ref<string>('');

const props = defineProps<{
    reportResult?: BAVReturnData;
    currentReportId?: string;
    hasError: boolean;
    stillProcessing: boolean;
}>();

const showReportSections = computed(() => !props.hasError && !props.stillProcessing);

if (props.currentReportId && showReportSections.value) {
    bavService.downloadReportById(props.currentReportId).then(url => (reportUrl.value = url));
}
const isBusinessReport = computed(() => !!props.reportResult?.account_details?.business_information?.ein);

const mergedSourceStatus = computed(() => {
    // look at the reportResult.banking_information.wire_routing.source and reportResult.banking_information.account_number.source
    // if either is not 'automatic', return 'manual'
    // otherwise return 'automatic'
    return props.reportResult?.banking_information?.wire_routing_number.source === 'automatic' &&
        props.reportResult?.banking_information?.account_number.source === 'automatic'
        ? 'automatic'
        : 'manual';
});

onBeforeUnmount(() => {
    URL.revokeObjectURL(reportUrl.value);
    reportUrl.value = '';
});
</script>
